/* Sprint G · Frontend Web Push helper.
 *
 * Uso:
 *   import { enablePush, disablePush, getPushStatus } from "@/lib/push";
 *   await enablePush({ role: "customer" });   // o "user"
 *
 * Detecta soporte, registra /sw.js, pide permisos, crea PushSubscription
 * con la VAPID key del backend y la envía a /api/push/subscribe-as-{role}.
 */
import { api, BACKEND_URL } from "./api";
import { customerApi } from "./customerAuth";

const SW_URL = "/sw.js";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: "default", subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    subscribed = !!sub;
  } catch (e) {}
  return { supported: true, permission, subscribed };
}

async function fetchVapidPublicKey() {
  const res = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error("VAPID indisponible");
  const j = await res.json();
  return j.public_key;
}

/**
 * Activa las notificaciones push para el usuario actual.
 * @param {Object} opts
 * @param {"customer"|"user"} opts.role  Customer (cliente final) o User (taller)
 */
export async function enablePush({ role = "user" } = {}) {
  if (!isPushSupported()) throw new Error("Tu navegador no soporta notificaciones push.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permiso de notificaciones denegado.");

  const reg = await registerServiceWorker();
  if (!reg) throw new Error("No se pudo registrar el Service Worker.");
  // Espera a que esté activo
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const publicKey = await fetchVapidPublicKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = sub.toJSON();
  const payload = { endpoint: json.endpoint, keys: json.keys };
  const path = role === "customer" ? "/push/subscribe-as-customer" : "/push/subscribe-as-user";
  const client = role === "customer" ? customerApi : api;
  await client.post(path, payload);
  return { ok: true };
}

/** Cancela la suscripción local y la limpia en backend. */
export async function disablePush({ role = "user" } = {}) {
  if (!isPushSupported()) return { ok: true };
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) await sub.unsubscribe();
  } catch (e) {}
  try {
    const path = role === "customer" ? "/push/unsubscribe-as-customer" : "/push/unsubscribe-as-user";
    const client = role === "customer" ? customerApi : api;
    await client.delete(path);
  } catch (e) {}
  return { ok: true };
}
